import type {
  AchievementWithHolders,
  Format,
  HeadToHeadResponse,
  MatchSummary,
  PaginatedMatches,
  PlayerProfile,
  PlayerSummary,
  RatingHistoryResponse,
  SeasonSummary,
  StandingsFormat,
  StandingsResponse,
  SubmitMatchInput,
} from '@foosball/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';

export function useAchievementCatalogue() {
  return useQuery({
    queryKey: ['achievements'],
    queryFn: async () =>
      (await api.get<{ achievements: AchievementWithHolders[] }>('/api/achievements')).achievements,
    staleTime: 5 * 60_000,
  });
}

export function usePlayerProfile(playerId: number | null) {
  return useQuery({
    queryKey: ['player', playerId],
    queryFn: () => api.get<PlayerProfile>(`/api/players/${playerId}`),
    enabled: playerId !== null,
  });
}

export function useRatingHistory(playerId: number | null, format: Format) {
  return useQuery({
    queryKey: ['player', playerId, 'ratings', format],
    queryFn: () =>
      api.get<RatingHistoryResponse>(`/api/players/${playerId}/ratings?format=${format}`),
    enabled: playerId !== null,
  });
}

export function useHeadToHead(playerId: number | null, opponentId: number | null) {
  return useQuery({
    queryKey: ['player', playerId, 'h2h', opponentId],
    queryFn: () => api.get<HeadToHeadResponse>(`/api/players/${playerId}/h2h/${opponentId}`),
    enabled: playerId !== null && opponentId !== null && playerId !== opponentId,
  });
}

export function useSeasons() {
  return useQuery({
    queryKey: ['seasons'],
    queryFn: async () => (await api.get<{ seasons: SeasonSummary[] }>('/api/seasons')).seasons,
    staleTime: 60_000,
  });
}

export function useStandings(seasonId: number | null, format: StandingsFormat) {
  return useQuery({
    queryKey: ['standings', seasonId, format],
    queryFn: () =>
      api.get<StandingsResponse>(`/api/seasons/${seasonId}/standings?format=${format}`),
    // Skip until a season is known, rather than requesting /api/seasons/null/…
    enabled: seasonId !== null,
  });
}

export function usePlayers() {
  return useQuery({
    queryKey: ['players'],
    queryFn: async () => (await api.get<{ players: PlayerSummary[] }>('/api/players')).players,
    staleTime: 60_000,
  });
}

export function useActiveSeason() {
  return useQuery({
    queryKey: ['season', 'active'],
    queryFn: async () =>
      (await api.get<{ season: SeasonSummary | null }>('/api/seasons/active')).season,
    staleTime: 60_000,
  });
}

/** Drives the nav badge, so confirmations are not forgotten. */
export function useAwaitingMe() {
  return useQuery({
    queryKey: ['matches', 'awaiting-me'],
    queryFn: () => api.get<{ matches: MatchSummary[]; count: number }>('/api/matches/awaiting-me'),
    refetchInterval: 60_000,
  });
}

export function useMatches(params: { status?: string; playerId?: number } = {}) {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.playerId) search.set('playerId', String(params.playerId));
  const suffix = search.toString() ? `?${search.toString()}` : '';

  return useQuery({
    queryKey: ['matches', 'list', params],
    queryFn: () => api.get<PaginatedMatches>(`/api/matches${suffix}`),
  });
}

/**
 * Every match mutation invalidates the same keys: a result changes the pending
 * list, the history and the ladder all at once.
 */
function useMatchInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: ['matches'] });
    void queryClient.invalidateQueries({ queryKey: ['players'] });
    void queryClient.invalidateQueries({ queryKey: ['standings'] });
    // Confirming applies ratings and may award achievements, so a cached
    // profile is stale the moment a result goes through.
    void queryClient.invalidateQueries({ queryKey: ['player'] });
    void queryClient.invalidateQueries({ queryKey: ['achievements'] });
  };
}

export function useSubmitMatch() {
  const invalidate = useMatchInvalidation();

  return useMutation({
    mutationFn: (input: SubmitMatchInput) => api.post<MatchSummary>('/api/matches', input),
    onSuccess: invalidate,
  });
}

export function useConfirmMatch() {
  const invalidate = useMatchInvalidation();

  return useMutation({
    mutationFn: (matchId: number) => api.post<MatchSummary>(`/api/matches/${matchId}/confirm`),
    onSuccess: invalidate,
  });
}

export function useDisputeMatch() {
  const invalidate = useMatchInvalidation();

  return useMutation({
    mutationFn: ({ matchId, reason }: { matchId: number; reason: string }) =>
      api.post<MatchSummary>(`/api/matches/${matchId}/dispute`, { reason }),
    onSuccess: invalidate,
  });
}

// --- admin -------------------------------------------------------------

export interface AdminUser {
  id: number;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  role: 'player' | 'admin';
  status: 'active' | 'inactive';
  createdAt: string;
}

export function useAdminUsers(enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => (await api.get<{ users: AdminUser[] }>('/api/admin/users')).users,
    enabled,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      ...changes
    }: {
      userId: number;
      role?: 'player' | 'admin';
      status?: 'active' | 'inactive';
    }) => api.patch<{ user: AdminUser }>(`/api/admin/users/${userId}`, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['players'] });
    },
  });
}

export function useVoidMatch() {
  const invalidate = useMatchInvalidation();

  return useMutation({
    mutationFn: (matchId: number) => api.post<MatchSummary>(`/api/matches/${matchId}/void`),
    onSuccess: invalidate,
  });
}

export interface RecalculationResult {
  matchesReplayed: number;
  ratingsWritten: number;
  durationMs: number;
}

export function useRecalculate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<RecalculationResult>('/api/admin/recalculate'),
    // A replay rewrites every rating and snapshot, so nothing cached survives.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useCreateSeason() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { name: string; startsOn: string; winScore?: number }) =>
      api.post<SeasonSummary>('/api/seasons', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seasons'] });
      void queryClient.invalidateQueries({ queryKey: ['season'] });
      void queryClient.invalidateQueries({ queryKey: ['standings'] });
    },
  });
}

export function useUpdateSeason() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ seasonId, ...changes }: { seasonId: number; isActive?: boolean }) =>
      api.patch<SeasonSummary>(`/api/seasons/${seasonId}`, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seasons'] });
      void queryClient.invalidateQueries({ queryKey: ['season'] });
      void queryClient.invalidateQueries({ queryKey: ['achievements'] });
      void queryClient.invalidateQueries({ queryKey: ['player'] });
    },
  });
}
